import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import '../styles/components/DustTextReveal.css';

/**
 * DustTextReveal
 * Each character animates in from a randomly scattered position,
 * blurred and faded — like cosmic dust particles coalescing to form letters.
 *
 * Props:
 *   scrollTrigger {boolean} — if true, fires on scroll into view (whileInView).
 *                             If false (default), fires immediately on mount.
 */
const DustTextReveal = ({
  text,
  delay = 0,
  className = '',
  charDuration = 0.55,
  stagger = 0.04,
  scrollTrigger = false,
}) => {
  const chars = useMemo(() => text.split(''), [text]);

  const seeds = useMemo(
    () =>
      chars.map(() => ({
        x: -(Math.random() * 18 + 6),
        y: (Math.random() - 0.5) * 10,
        rotate: (Math.random() - 0.5) * 20,
        blur: Math.random() * 6 + 6,
        scale: Math.random() * 0.3 + 0.6,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text]
  );

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger,
        delayChildren: delay,
      },
    },
  };

  const renderChars = () =>
    chars.map((char, i) => {
      const seed = seeds[i];
      const charVariants = {
        hidden: {
          opacity: 0,
          x: seed.x,
          y: seed.y,
          rotate: seed.rotate,
          scale: seed.scale,
          filter: `blur(${seed.blur}px)`,
        },
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          rotate: 0,
          scale: 1,
          filter: 'blur(0px)',
          transition: {
            duration: charDuration,
            ease: [0.25, 0.46, 0.45, 0.94],
          },
        },
      };
      return (
        <motion.span
          key={i}
          className="dust-char"
          variants={charVariants}
          aria-hidden="true"
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      );
    });

  if (scrollTrigger) {
    return (
      <motion.span
        className={`dust-text-container ${className}`}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.4 }}
        aria-label={text}
      >
        {renderChars()}
      </motion.span>
    );
  }

  return (
    <motion.span
      className={`dust-text-container ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      aria-label={text}
    >
      {renderChars()}
    </motion.span>
  );
};

export default DustTextReveal;
